import type { ComponentChildren } from "preact";
import { useEffect, useRef } from "preact/hooks";

interface Props {
  title: string;
  onClose: () => void;
  /** "panel": a side panel in landscape, a full sheet in portrait (the Entry viewer). "card": a centred card (statistic panels). */
  kind?: "panel" | "card";
  /** Extra class on the dialog element. */
  class?: string;
  /** Controls placed in the header before the close button. */
  header?: ComponentChildren;
  children: ComponentChildren;
}

/**
 * A modal layer: a backdrop that closes on tap, Escape closes, the body stops scrolling behind it,
 * and the dialog scrolls on its own. Every control inside keeps the 44 pt target the sheet uses.
 */
export function Modal({ title, onClose, kind = "card", class: extra, header, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    addEventListener("keydown", onKey);
    document.body.classList.add("modal-open");
    return () => {
      removeEventListener("keydown", onKey);
      if (document.querySelectorAll(".modal-backdrop").length <= 1) document.body.classList.remove("modal-open");
    };
  }, [onClose]);
  return (
    <div class="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} class={`modal modal-${kind}${extra ? ` ${extra}` : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <div class="modal-header">
          <h2 class="modal-title">{title}</h2>
          {header}
          <button type="button" class="icon close" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div class="modal-body">{children}</div>
      </div>
    </div>
  );
}
